import { LegalPageLayout, P, UL, LI, Strong, H3, ContactLink } from './LegalPageLayout';

/**
 * Data Processing Addendum (個人情報保護法 / APPI lead, plus GDPR Art. 28
 * + UK GDPR + CCPA addenda). Customers may accept this DPA in-product (a
 * click-to-sign record is written to the tamper-evident audit log) or
 * request a counter-signed copy by emailing legal@plyez.com.
 */
export function DpaPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.dpa"
      lastUpdated="2026-06-03"
      lead={
        <>
          This Data Processing Addendum (the &ldquo;DPA&rdquo;) forms part of the
          PlyEZ Terms of Service between the customer (&ldquo;Customer&rdquo;) and{' '}
          <Strong>[registered company name]</Strong>, a Japanese 株式会社 (K.K.)
          (&ldquo;PlyEZ&rdquo; or the &ldquo;Company&rdquo;), and applies whenever
          Customer&rsquo;s use of the PlyEZ service (the &ldquo;Service&rdquo;)
          involves PlyEZ processing Personal Data on Customer&rsquo;s behalf. It is
          designed to satisfy the Japan Act on the Protection of Personal Information
          (個人情報保護法 / APPI) as the lead framework, together with Article 28 of
          the EU GDPR, the UK GDPR, and the CCPA/CPRA where those laws apply to a
          given Customer or data subject.
        </>
      }
      sections={[
        {
          id: 'definitions',
          title: '1. Definitions',
          body: (
            <>
              <P>
                Capitalised terms not defined here have the meaning given in the
                Terms of Service. In this DPA:
              </P>
              <UL>
                <LI>
                  &ldquo;<Strong>Applicable Data Protection Law</Strong>&rdquo; means
                  the APPI and, to the extent applicable to a Customer or data
                  subject, the EU GDPR, the UK GDPR, the Swiss FADP, and the
                  CCPA/CPRA, in each case as amended.
                </LI>
                <LI>
                  &ldquo;<Strong>Controller</Strong>&rdquo;,
                  &ldquo;<Strong>Processor</Strong>&rdquo;,
                  &ldquo;<Strong>Data Subject</Strong>&rdquo;,
                  &ldquo;<Strong>Personal Data</Strong>&rdquo;, and
                  &ldquo;<Strong>Processing</Strong>&rdquo; have the meanings given in
                  the GDPR; the corresponding APPI concepts
                  (個人情報取扱事業者, 個人データ, 本人) apply for Japanese-law purposes.
                </LI>
                <LI>
                  &ldquo;<Strong>Customer Personal Data</Strong>&rdquo; means Personal
                  Data that PlyEZ Processes on Customer&rsquo;s behalf under the Terms
                  of Service.
                </LI>
                <LI>
                  &ldquo;<Strong>Sub-processor</Strong>&rdquo; means any third party
                  engaged by PlyEZ to Process Customer Personal Data.
                </LI>
                <LI>
                  &ldquo;<Strong>SCCs</Strong>&rdquo; means the Standard Contractual
                  Clauses approved by the European Commission in Decision (EU)
                  2021/914.
                </LI>
              </UL>
            </>
          ),
        },
        {
          id: 'roles',
          title: '2. Roles of the parties',
          body: (
            <>
              <P>
                With respect to Customer Personal Data, Customer is the{' '}
                <Strong>Controller</Strong> (or, where Customer itself acts as a
                processor for a third party, the processor) and PlyEZ is the{' '}
                <Strong>Processor</Strong> (or sub-processor). Under the APPI, PlyEZ
                acts as an entity entrusted with the handling of personal data
                (個人データの取扱いの委託を受けた者) under Article 25.
              </P>
              <P>
                PlyEZ will Process Customer Personal Data only on Customer&rsquo;s
                documented instructions, which comprise the Terms of Service, this
                DPA, and Customer&rsquo;s configuration and use of the Service,
                unless Processing is required by a law to which PlyEZ is subject — in
                which case PlyEZ will inform Customer of that legal requirement before
                Processing, unless that law prohibits such notice on important grounds
                of public interest. PlyEZ does not sell Customer Personal Data and
                does not use it to train AI models.
              </P>
            </>
          ),
        },
        {
          id: 'scope',
          title: '3. Subject-matter, duration, nature and purpose',
          body: (
            <>
              <UL>
                <LI>
                  <Strong>Subject-matter</Strong>: PlyEZ&rsquo;s provision of the
                  Service — AI-native back-office automation covering accounts
                  payable and receivable, expense management, payments, ledger,
                  Japanese tax compliance, and cross-border workflows.
                </LI>
                <LI>
                  <Strong>Duration</Strong>: for the term of the Terms of Service,
                  plus the post-termination return/deletion period in Section&nbsp;11.
                </LI>
                <LI>
                  <Strong>Nature</Strong>: collection, structuring, storage,
                  retrieval, AI-assisted extraction and classification, transmission
                  to and from payment and e-invoicing networks, and erasure of
                  Customer Personal Data.
                </LI>
                <LI>
                  <Strong>Purpose</Strong>: to provide, secure, maintain, and support
                  the Service for Customer in accordance with the Terms of Service.
                </LI>
              </UL>
            </>
          ),
        },
        {
          id: 'categories',
          title: '4. Categories of Data Subjects and Personal Data',
          body: (
            <>
              <H3>Categories of Data Subjects</H3>
              <UL>
                <LI>Customer&rsquo;s personnel, administrators, and invited users;</LI>
                <LI>Customer&rsquo;s vendors, suppliers, and their representatives;</LI>
                <LI>Customer&rsquo;s customers and counterparties named on documents;</LI>
                <LI>Employees and contractors named on expense reports and payouts.</LI>
              </UL>
              <H3>Categories of Personal Data</H3>
              <UL>
                <LI>Identification and contact data (names, email addresses, telephone numbers, profile photos, OAuth subject identifiers);</LI>
                <LI>Authentication data (password hashes, session metadata, role and permission assignments);</LI>
                <LI>Business and financial data (vendor and counterparty banking details, tax identification numbers such as 法人番号 and 適格請求書 registration numbers, invoice and receipt line items, expense amounts);</LI>
                <LI>Document content uploaded as invoices, receipts, contracts, and attachments, including any Personal Data they contain;</LI>
                <LI>Usage and audit-log data (actor, action, timestamp, source IP).</LI>
              </UL>
              <P>
                The Service is intended for business records. Customer should not
                upload special categories of Personal Data (GDPR Art. 9) or
                要配慮個人情報 (APPI sensitive data) except where strictly necessary
                and lawful; PlyEZ does not Process such data for any independent
                purpose.
              </P>
            </>
          ),
        },
        {
          id: 'obligations',
          title: '5. Processor obligations',
          body: (
            <>
              <P>PlyEZ will:</P>
              <UL>
                <LI><Strong>Process on instructions only</Strong> — Process Customer Personal Data solely on Customer&rsquo;s documented instructions, including with regard to international transfers;</LI>
                <LI><Strong>Confidentiality</Strong> — ensure that personnel authorised to Process Customer Personal Data are bound by appropriate confidentiality obligations and are subject to least-privilege access;</LI>
                <LI><Strong>Security</Strong> — implement and maintain the technical and organisational measures set out in Annex&nbsp;II (Section&nbsp;14);</LI>
                <LI><Strong>Assist with Data Subject rights</Strong> — taking into account the nature of the Processing, assist Customer by appropriate measures (including the Service&rsquo;s self-serve access, export, correction, and deletion tools) in responding to requests to exercise Data Subject rights under Applicable Data Protection Law;</LI>
                <LI><Strong>Assist with compliance</Strong> — assist Customer in ensuring compliance with security, breach-notification, data-protection-impact-assessment (DPIA), and prior-consultation obligations (GDPR Arts. 32–36), taking into account the information available to PlyEZ;</LI>
                <LI><Strong>Make information available</Strong> — make available the information necessary to demonstrate compliance with this DPA and Article 28 GDPR, as further described in Section&nbsp;10.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'subprocessors',
          title: '6. Sub-processing',
          body: (
            <>
              <P>
                Customer provides a general authorisation for PlyEZ to engage
                Sub-processors to Process Customer Personal Data. The current list of
                Sub-processors is published at{' '}
                <ContactLink to="/legal/subprocessors">/legal/subprocessors</ContactLink>{' '}
                and is incorporated into this DPA by reference.
              </P>
              <P>
                PlyEZ will impose data-protection obligations on each Sub-processor
                that are no less protective than those in this DPA, and remains liable
                to Customer for each Sub-processor&rsquo;s performance of its
                obligations. PlyEZ will give Customer at least{' '}
                <Strong>30 days&rsquo; notice</Strong> before adding or replacing a
                Sub-processor (via in-app notification and email to the address
                subscribed at privacy@plyez.com). Customer may object on reasonable,
                data-protection-related grounds within that period; if the parties
                cannot resolve the objection, Customer may terminate the affected
                portion of the Service as its sole remedy.
              </P>
            </>
          ),
        },
        {
          id: 'transfers',
          title: '7. International transfers',
          body: (
            <>
              <P>
                PlyEZ is established in Japan, which the European Commission has
                recognised as providing an adequate level of protection for transfers
                from the EEA. Where Customer Personal Data is nonetheless transferred
                from the EEA, the United Kingdom, or Switzerland to a country without
                an adequacy decision, the following apply:
              </P>
              <UL>
                <LI><Strong>EEA</Strong> — the SCCs, <Strong>Module Two (controller-to-processor)</Strong>, are incorporated by reference and deemed executed by the parties, with PlyEZ as data importer and Customer as data exporter. The optional docking clause applies; the audit clause (8.9) is satisfied via Section&nbsp;10; the governing law is the law of Ireland and the supervisory authority is determined by Annex&nbsp;I; and Annex&nbsp;I and Annex&nbsp;II of this DPA populate the SCC annexes.</LI>
                <LI><Strong>United Kingdom</Strong> — the UK International Data Transfer Addendum to the SCCs (the &ldquo;UK Addendum&rdquo;) issued by the ICO applies and is incorporated by reference, with the SCCs completed as above.</LI>
                <LI><Strong>Switzerland</Strong> — the SCCs apply as amended for the Swiss FADP, with the FDPIC as supervisory authority and references to the GDPR read as references to the FADP.</LI>
                <LI><Strong>APPI cross-border</Strong> — for transfers governed by the APPI, PlyEZ provides equivalent protection through contractual measures consistent with APPI Article 28 and PPC guidance, and makes available on request the information needed for Customer&rsquo;s notice to data subjects.</LI>
              </UL>
              <P>
                PlyEZ applies the supplementary measures in Annex&nbsp;II (encryption
                in transit and at rest, access controls, and a documented practice of
                challenging unlawful or overbroad government access requests). Where
                this DPA conflicts with the SCCs or the UK Addendum, the SCCs or UK
                Addendum prevail with respect to the relevant transfer.
              </P>
            </>
          ),
        },
        {
          id: 'dsar',
          title: '8. Data Subject requests',
          body: (
            <P>
              The Service provides self-serve tools that let Customer&rsquo;s
              administrators access, correct, export, and delete Personal Data for
              their users and records. If PlyEZ receives a request directly from a
              Data Subject relating to Customer Personal Data, PlyEZ will not respond
              to the request itself (except to confirm that the request relates to
              Customer) and will, without undue delay, forward it to Customer. Where a
              request cannot be satisfied through self-serve tools, PlyEZ will provide
              reasonable assistance on Customer&rsquo;s instruction. Contact{' '}
              <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>.
            </P>
          ),
        },
        {
          id: 'breach',
          title: '9. Personal Data breach notification',
          body: (
            <P>
              PlyEZ will notify Customer <Strong>without undue delay, and in any
              event within 72 hours</Strong>, after becoming aware of a Personal Data
              breach affecting Customer Personal Data. The notification will describe,
              to the extent known, the nature of the breach, the categories and
              approximate number of Data Subjects and records concerned, the likely
              consequences, and the measures taken or proposed to address it (as
              required by GDPR Art. 33(3)), and will be supplemented as further
              information becomes available. PlyEZ will reasonably cooperate with
              Customer and any competent authority (including the Japan Personal
              Information Protection Commission, 個人情報保護委員会) in investigating
              and remediating the breach. Reports may be sent to{' '}
              <a className="text-accent-700 underline" href="mailto:security@plyez.com">security@plyez.com</a>.
            </P>
          ),
        },
        {
          id: 'audits',
          title: '10. Audit rights',
          body: (
            <P>
              PlyEZ will make available to Customer all information reasonably
              necessary to demonstrate compliance with this DPA and Article 28 GDPR,
              and will allow for and contribute to audits, including inspections,
              conducted by Customer or an independent auditor mandated by Customer.
              PlyEZ may satisfy this obligation by providing its then-current security
              documentation and, when available, third-party audit reports. (SOC 2
              Type&nbsp;I/II and ISO&nbsp;27001 / ISMS certification are on PlyEZ&rsquo;s
              roadmap and in progress; PlyEZ does not represent that it is currently
              certified.) On at least 30 days&rsquo; written notice, no more than once
              per twelve-month period (or more frequently if required by a supervisory
              authority or following a breach), Customer may conduct an audit during
              normal business hours, subject to confidentiality and to not unreasonably
              disrupting PlyEZ&rsquo;s operations, at Customer&rsquo;s expense.
            </P>
          ),
        },
        {
          id: 'deletion',
          title: '11. Return and deletion on termination',
          body: (
            <P>
              On termination or expiry of the Terms of Service, PlyEZ will, at
              Customer&rsquo;s election, delete or return all Customer Personal Data
              and delete existing copies, unless storage is required by a law to which
              PlyEZ is subject. Customer may export its data at any time through
              Settings → Export and the accounting connectors. Certain records (e.g.,
              invoice and bookkeeping records) are subject to a 7-year retention
              requirement under Japan&rsquo;s Electronic Books Preservation Act
              (電子帳簿保存法); where PlyEZ retains such data to meet that obligation, it
              will keep it isolated, secured under Annex&nbsp;II, and Process it solely
              for that legal purpose until deletion is permitted.
            </P>
          ),
        },
        {
          id: 'liability',
          title: '12. Liability and governing law',
          body: (
            <P>
              Each party&rsquo;s liability arising out of or related to this DPA is
              subject to the limitations and exclusions of liability set out in the
              Terms of Service, and any reference to a party&rsquo;s liability means
              the aggregate liability of that party under the Terms of Service and this
              DPA together. Except where the SCCs or UK Addendum require otherwise for
              a particular transfer, this DPA is governed by the laws of{' '}
              <Strong>Japan</Strong>, and the parties submit to the exclusive
              jurisdiction of the <Strong>Tokyo District Court (東京地方裁判所)</Strong>{' '}
              as the court of first instance. If any provision of this DPA conflicts
              with the Terms of Service, this DPA prevails with respect to the Processing
              of Customer Personal Data.
            </P>
          ),
        },
        {
          id: 'execution',
          title: '13. Execution',
          body: (
            <P>
              Customer accepts this DPA by accepting the Terms of Service, by clicking
              to sign within the Service (a record of which is written to the
              tamper-evident audit log), or by continuing to use the Service after this
              DPA takes effect. Customers requiring a separately counter-signed copy may
              request one by emailing{' '}
              <a className="text-accent-700 underline" href="mailto:legal@plyez.com">legal@plyez.com</a>.
            </P>
          ),
        },
        {
          id: 'annex-1',
          title: 'Annex I — Parties and details of Processing',
          body: (
            <>
              <H3>A. List of parties</H3>
              <UL>
                <LI><Strong>Data exporter / Controller</Strong>: Customer, as identified in its PlyEZ account and the Terms of Service. Role: Controller. Contact: the account administrator and the privacy contact on file.</LI>
                <LI><Strong>Data importer / Processor</Strong>: [registered company name], a Japanese 株式会社 (K.K.), of [registered address]. Role: Processor. Contact: Data Protection Officer, <a className="text-accent-700 underline" href="mailto:dpo@plyez.com">dpo@plyez.com</a>.</LI>
              </UL>
              <H3>B. Description of transfer</H3>
              <UL>
                <LI><Strong>Categories of Data Subjects</Strong>: as set out in Section&nbsp;4.</LI>
                <LI><Strong>Categories of Personal Data</Strong>: as set out in Section&nbsp;4.</LI>
                <LI><Strong>Sensitive data</Strong>: not intended; if present, protected by the measures in Annex&nbsp;II.</LI>
                <LI><Strong>Frequency</Strong>: continuous, for the duration of the Service.</LI>
                <LI><Strong>Nature and purpose</Strong>: as set out in Section&nbsp;3.</LI>
                <LI><Strong>Retention</Strong>: for the term plus the period in Section&nbsp;11; statutory records retained for 7 years per 電子帳簿保存法.</LI>
                <LI><Strong>Sub-processors</Strong>: the transfer details for each are described at <ContactLink to="/legal/subprocessors">/legal/subprocessors</ContactLink>.</LI>
              </UL>
              <H3>C. Competent supervisory authority</H3>
              <P>
                For the SCCs, the competent supervisory authority is that of the EEA
                Member State in which the data exporter is established or its EU
                representative is located. For the APPI, the competent authority is the
                Personal Information Protection Commission of Japan
                (個人情報保護委員会). For the UK, the Information Commissioner&rsquo;s
                Office (ICO).
              </P>
            </>
          ),
        },
        {
          id: 'annex-2',
          title: 'Annex II — Technical and organisational security measures',
          body: (
            <>
              <P>
                PlyEZ implements and maintains at least the following measures, kept
                under review as the state of the art and risks evolve:
              </P>
              <H3>Encryption</H3>
              <UL>
                <LI>Encryption at rest using <Strong>AES-256-GCM</Strong> with versioned envelope keys, applied to sensitive fields including vendor tax identification numbers and banking details;</LI>
                <LI><Strong>TLS 1.3</Strong> on every external endpoint for data in transit.</LI>
              </UL>
              <H3>Access control and confidentiality</H3>
              <UL>
                <LI>Role-based access control and least-privilege staff access on a need-to-know basis;</LI>
                <LI>Strong password hashing for stored credentials and support for federated sign-in;</LI>
                <LI>Confidentiality obligations binding all personnel with access to Customer Personal Data.</LI>
              </UL>
              <H3>Integrity and auditability</H3>
              <UL>
                <LI>A <Strong>tamper-evident, hash-chained audit log</Strong> recording actor, action, timestamp, and source for security-relevant events;</LI>
                <LI>Optional RFC&nbsp;3161 trusted timestamping via an accredited TSA for document integrity.</LI>
              </UL>
              <H3>Availability and resilience</H3>
              <UL>
                <LI>Regular encrypted backups with defined retention, and restoration procedures that are periodically tested;</LI>
                <LI>Hosting with EU-based infrastructure (Hetzner, Germany) and the option of EU-jurisdiction object storage (Cloudflare R2).</LI>
              </UL>
              <H3>Data governance and retention</H3>
              <UL>
                <LI><Strong>7-year retention</Strong> of statutory document records in line with Japan&rsquo;s 電子帳簿保存法, with controlled deletion thereafter;</LI>
                <LI>Documented Sub-processor management and change-notification process (Section&nbsp;6);</LI>
                <LI>Logical separation of Customer data and environment hardening.</LI>
              </UL>
            </>
          ),
        },
      ]}
    />
  );
}
